import type { LinearRGB } from '../../../core/color';
import type { Landmark } from '../../../data/types';
import type { Vec2 } from '../../../geo/types';
import { PATTERN } from '../../../render/patterns/patternIds';
import { SURFACE } from '../../mesh/attributes';
import type { MeshBuilder } from '../../mesh/meshBuilder';
import { horizontalCap, pyramidOverRing, ringPoints } from '../solids';
import { buildVessel } from './churchVessel';
import { buildConventEnclosure } from './conventEnclosure';
import { buildConventRange } from './conventRange';
import { buildConventSoil } from './conventSoil';
import { buildConventTrees } from './conventTrees';
import type { LandmarkContext } from './landmarkFeature';
import { centredWindow, rectWall } from './masonry';
import {
  nieuweNonnenChapel,
  nieuweNonnenFloors,
  nieuweNonnenFrame,
  nieuweNonnenRangeFrame,
} from './nieuweNonnenPlan';
import reading from './nieuweNonnenPlan.json';
import { paintedChapelStyles, type PaintedChapelColors } from './paintedChapels';
import { meshFrameOf, toLayout } from './planFrame';
import { NO_TRACERY, type TracerySink } from './tracery/tracerySink';

/** Nieuwe Nonnen: source-read bodies, an open court and fruit ground behind the cross-wing. */
export function buildNieuweNonnen(
  builder: MeshBuilder,
  landmark: Landmark,
  colors: PaintedChapelColors & {
    tile: LinearRGB;
    ground: LinearRGB;
    garden: LinearRGB;
    timber: LinearRGB;
    foliage: LinearRGB;
  },
  ids: { readonly edgeId: number; readonly seed: number },
  tracery: TracerySink = NO_TRACERY,
  context: Pick<LandmarkContext, 'elevation' | 'elevationAt'> = {}
): void {
  const plan = nieuweNonnenFrame(landmark),
    frame = meshFrameOf(plan),
    styles = paintedChapelStyles(colors, ids, tracery),
    brick = { ...styles, wall: { ...styles.wall, surface: SURFACE.brick } },
    chapel = nieuweNonnenChapel();
  buildVessel(builder, brick, frame, chapel, {
    longWindows: { sill: 3, top: chapel.eaveY - 1.2 },
    westWindow: true,
    bay: 6.2,
  });
  const bell = reading.belfry,
    ring = ringPoints([bell.u, bell.v], bell.radius, 8, Math.PI / 8);
  ring.forEach((from, i) => {
    const to = ring[(i + 1) % ring.length]!,
      width = Math.hypot(to[0] - from[0], to[1] - from[1]);
    rectWall(
      builder,
      brick,
      frame,
      [from, to],
      [bell.foot, bell.eaves],
      centredWindow(width, 0.45, bell.eaves - bell.foot - 0.3, 0.5),
      bell.foot
    );
  });
  horizontalCap(builder, brick.wall, frame, ring, bell.foot, false);
  pyramidOverRing(builder, brick, frame, ring, bell.eaves, { plan: [bell.u, bell.v], y: bell.tip });
  for (const part of reading.ranges)
    buildConventRange(
      builder,
      meshFrameOf(nieuweNonnenRangeFrame(plan, part)),
      part.roof === 'tile'
        ? {
            ...brick,
            roof: {
              ...brick.roof,
              color: colors.tile,
              surface: SURFACE.roofTile,
              pattern: PATTERN.roofTileHollePan,
            },
          }
        : brick,
      part
    );
  const base = context.elevation ?? 0,
    terrain = context.elevationAt ?? (() => base),
    localGround = (p: Vec2) => terrain(toLayout(plan, p)) - base;
  buildConventEnclosure(builder, brick.wall, frame, localGround, reading.walls);
  buildConventTrees(
    builder,
    { ...brick.wall, pattern: PATTERN.none },
    { ...brick.wall, pattern: PATTERN.none },
    frame,
    colors,
    localGround,
    reading.trees.map((p): Vec2 => [p[0]!, p[1]!]),
    'nieuwe-nonnen'
  );
  for (const floor of nieuweNonnenFloors(landmark))
    buildConventSoil(
      builder,
      { ...brick.wall, color: floor.garden ? colors.garden : colors.ground },
      floor.ring,
      terrain,
      base,
      floor.height,
      floor.garden
    );
}
